import { createRoute } from '@tanstack/react-router';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@cca/ui';
import { useRuntimeConfig } from '../lib/runtime-config.js';
import { rootRoute } from './root.js';

/**
 * Overview.
 *
 * At this milestone the only real data available is the runtime safety posture,
 * so that is what this page shows — accurately. Operational counters (unanswered
 * email, orders needing attention, shipment exceptions, pending approvals) are
 * added as the milestone that produces each data source lands; they are not
 * rendered as zeros or placeholders before then, because a confident "0
 * exceptions" from a system that cannot yet detect exceptions is a lie.
 */

function FlagRow({ label, enabled }: { readonly label: string; readonly enabled: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
      <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
      <Badge tone={enabled ? 'success' : 'neutral'}>{enabled ? 'Enabled' : 'Disabled'}</Badge>
    </div>
  );
}

function OverviewPage() {
  const config = useRuntimeConfig();
  const sendingArmed = config.flags.ALLOW_EXTERNAL_WRITES && !config.simulatorEnabled;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Overview</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {config.product.name} — operational status for this environment.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Sending posture</CardTitle>
          <CardDescription>
            Two independent switches must both be on before any message can reach a real recipient.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={
              sendingArmed
                ? 'rounded-md bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200'
                : 'rounded-md bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200'
            }
          >
            {sendingArmed
              ? 'External sending is ARMED. Approved actions will reach real recipients.'
              : 'External sending is disarmed. Nothing leaves the platform.'}
          </div>
          <div className="mt-3">
            <FlagRow
              label="Allow external writes (global)"
              enabled={config.flags.ALLOW_EXTERNAL_WRITES}
            />
            <FlagRow
              label="Transactional email sending"
              enabled={config.flags.EMAIL_SEND_ENABLED}
            />
            <FlagRow label="Campaign sending" enabled={config.flags.CAMPAIGN_SEND_ENABLED} />
            <FlagRow label="Shopify writes" enabled={config.flags.SHOPIFY_WRITE_ENABLED} />
            <FlagRow label="AI agent" enabled={config.flags.AI_AGENT_ENABLED} />
            <FlagRow
              label="AI automatic replies"
              enabled={config.flags.AI_AGENT_AUTO_REPLY_ENABLED}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Operational counters</CardTitle>
          <CardDescription>
            Unanswered messages, orders requiring attention, shipment exceptions, drafts awaiting
            review, pending approvals and scheduled campaigns appear here as each data source is
            connected.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            No operational data source is connected in this build yet. Counters are deliberately
            absent rather than shown as zero — see <code>PROJECT_STATUS.md</code> for the current
            milestone.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: OverviewPage,
});
